import React, { memo, useCallback } from 'react'

import {
  classnames,
  FavoriteFilled, FavoriteOutlined, Icon,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  func, number, string, oneOfType, bool, shape,
} from 'prop-types'

import { iconSize } from 'Pods/LazyLoaded/CommunityGalleryPanel/constants'
import CreatorAvatar from 'Pods/Shared/CreatorAvatar/CreatorAvatar'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

import { NOOP } from 'Utils/stableDefaults'

// The remix button holds only the image, keeping one clean focusable control
// with no nested interactives for keyboard and screen-reader users. The creator
// avatar (which carries hover-tooltip DOM) and the like toggle render as sibling
// overlays positioned over the card, never inside the button.
function Card({
  id,
  slug = '',
  title,
  prompt,
  createdAt,
  image,
  user = null,
  likeCount = 0,
  liked = false,
  onToggleLike = NOOP,
  onRemix,
  eager = false,
}) {
  const t = useTranslation()
  // Default options: this card mounts late (the gallery panel is lazy-imported),
  // after /load-services.js already fired its config event, so read the
  // already-present window.pageConfig on mount rather than waiting for an event
  // that will not fire again (see useOpenedModelReadOnly for the same reasoning).
  const pageConfig = usePageConfig()
  const showCreatorAvatar = !!pageConfig?.aiCreatorAvatarEnabled

  const handleToggleLike = useCallback(() => {
    onToggleLike({ id, slug, liked, likeCount })
  }, [onToggleLike, id, slug, liked, likeCount])

  const handleRemix = useCallback(() => {
    // Pass the full surface area the downstream optimistic library entry and
    // viewer placeholder need; Card stays the only place that knows the field
    // names the BE returns. `slug` drives the stable detail URL.
    onRemix({ id, slug, title, prompt, createdAt, image })
  }, [onRemix, id, slug, title, prompt, createdAt, image])

  // The username is a hover-only affordance (see the tooltip below) and the
  // avatar is flag-gated, so name the creator in the card's accessible label to
  // keep the attribution reachable without hover and regardless of the flag.
  const remixLabel = user?.username
    ? t('community.card.remixBy', { title, username: user.username })
    : t('community.card.remix', { title })

  return (
    <div className="community-gallery-card">
      <button
        type="button"
        className="community-gallery-card__remix"
        onClick={handleRemix}
        aria-label={remixLabel}
      >
        <img
          className="community-gallery-card__image"
          src={image}
          alt=""
          aria-hidden="true"
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          fetchpriority={eager ? 'high' : 'low'}
        />
      </button>

      {/* Flag-gated (:enable_ai_creator_avatar). Renders as a sibling overlay, never
          inside the remix button, so the button keeps a single focusable
          control with no nested interactives. */}
      {showCreatorAvatar && (
        <CreatorAvatar
          user={user}
          tooltipContent={user?.username}
          id={`community-gallery-card-author-${id}`}
          place="right"
          containerClass="community-gallery-card__author"
          className="community-gallery-card__avatar"
        />
      )}

      {/* The comment count belongs here too, but ships with the comments modal. */}
      <button
        type="button"
        className="community-gallery-card__like"
        onClick={handleToggleLike}
        aria-pressed={liked}
        aria-label={liked ? t('like.unlikeModel', { title }) : t('like.likeModel', { title })}
      >
        <span className="community-gallery-card__like-count cgt-body-base-semibold">{likeCount}</span>
        <Icon
          svgComponent={liked ? FavoriteFilled : FavoriteOutlined}
          size={iconSize}
          color="dark"
          notSpaced
          className={classnames(
            'community-gallery-card__like-icon',
            liked && 'community-gallery-card__like-icon--filled'
          )}
        />
      </button>
    </div>
  )
}

Card.propTypes = {
  id: oneOfType([number, string]).isRequired,
  slug: string,
  title: string.isRequired,
  prompt: string.isRequired,
  createdAt: string.isRequired,
  image: string.isRequired,
  user: shape({ username: string, avatar_url: string }),
  likeCount: number,
  liked: bool,
  onToggleLike: func,
  onRemix: func.isRequired,
  eager: bool,
}

export default memo(Card)
